import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { automationsAPI } from '@/shared/lib/api';
import { automationSchema, cronPresets } from '@/shared/lib/validations';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Textarea } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { Switch } from '@travellegacy/nexops-design-system';
import { JsonEditor } from '@travellegacy/nexops-design-system';
import { useState, useEffect } from 'react';
import type {
  Automation,
  AxiosErrorResponse,
  Job,
  ApiResponse,
  AutomationFormData,
} from '@/shared/types';
import { AutomationType } from '@/shared/types';
import { Button } from '@travellegacy/nexops-design-system';
import { toast } from '@/shared/lib/toast';
import { Calendar, Webhook } from 'lucide-react';

interface AutomationFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  automation?: Automation;
}

interface AutomationFormInput extends AutomationFormData {
  // Form specific fields that get merged into config
  baseTimeMinutes?: number;
  timePerUnitMinutes?: number;
  config?: string;
}

interface CreateAutomationPayload {
  type: AutomationType;
  name: string;
  description?: string;
  slug?: string;
  isActive: boolean;
  schedule?: string;
  config?: Record<string, unknown>;
}

// Helper para ler métricas de tempo do config
function getTimeMetrics(config: Record<string, unknown> | null | undefined) {
  if (!config || typeof config !== 'object') {
    return { baseTimeMinutes: undefined, timePerUnitMinutes: undefined };
  }

  // Lê de configTimeSaved
  if (
    'configTimeSaved' in config &&
    typeof config.configTimeSaved === 'object' &&
    config.configTimeSaved !== null
  ) {
    const timeSaved = config.configTimeSaved as Record<string, unknown>;
    return {
      baseTimeMinutes:
        'baseTimeMinutes' in timeSaved && typeof timeSaved.baseTimeMinutes === 'number'
          ? timeSaved.baseTimeMinutes
          : undefined,
      timePerUnitMinutes:
        'timePerUnitMinutes' in timeSaved && typeof timeSaved.timePerUnitMinutes === 'number'
          ? timeSaved.timePerUnitMinutes
          : undefined,
    };
  }

  return { baseTimeMinutes: undefined, timePerUnitMinutes: undefined };
}

export function AutomationForm({ open, onOpenChange, automation }: AutomationFormProps) {
  const queryClient = useQueryClient();
  const isEdit = !!automation;
  const [cronPreset, setCronPreset] = useState<string>(
    automation?.schedule || cronPresets[0].value
  );

  // Fetch available jobs
  const { data: jobsResponse } = useQuery<ApiResponse<Job[]>>({
    queryKey: ['jobs'],
    queryFn: async () => {
      const response = await automationsAPI.listJobs();
      return response.data as ApiResponse<Job[]>;
    },
    enabled: open,
  });

  const jobs = jobsResponse?.data || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    control,
  } = useForm<AutomationFormInput>({
    resolver: zodResolver(automationSchema),
    defaultValues: automation
      ? {
          type: automation.type,
          name: automation.name,
          description: automation.description || '',
          slug: automation.slug || '',
          isActive: automation.isActive,
          schedule: automation.schedule || '',
          jobType:
            automation.config &&
            typeof automation.config === 'object' &&
            'jobType' in automation.config &&
            typeof automation.config.jobType === 'string'
              ? automation.config.jobType
              : '',
          ...getTimeMetrics(automation.config),
          config: JSON.stringify(automation.config, null, 2),
        }
      : {
          type: AutomationType.CRON,
          name: '',
          description: '',
          slug: '',
          isActive: true,
          schedule: cronPresets[0].value,
          jobType: '',
          baseTimeMinutes: undefined,
          timePerUnitMinutes: undefined,
          config: '{}',
        },
  });

  const type = watch('type');
  const isActive = watch('isActive');
  const schedule = watch('schedule');
  const jobType = watch('jobType');

  // Reset form when automation changes or modal opens
  useEffect(() => {
    if (open) {
      if (automation) {
        const formattedConfig = JSON.stringify(automation.config, null, 2);
        reset({
          type: automation.type,
          name: automation.name,
          description: automation.description || '',
          slug: automation.slug || '',
          isActive: automation.isActive,
          schedule: automation.schedule || '',
          jobType:
            automation.config &&
            typeof automation.config === 'object' &&
            'jobType' in automation.config &&
            typeof automation.config.jobType === 'string'
              ? automation.config.jobType
              : '',
          ...getTimeMetrics(automation.config),
          config: formattedConfig,
        });

        // Check if schedule matches a preset, otherwise set to 'custom'
        const matchingPreset = cronPresets.find((preset) => preset.value === automation.schedule);
        setCronPreset(matchingPreset ? automation.schedule || cronPresets[0].value : 'custom');
      } else {
        reset({
          type: AutomationType.CRON,
          name: '',
          description: '',
          slug: '',
          isActive: true,
          schedule: cronPresets[0].value,
          jobType: '',
          baseTimeMinutes: undefined,
          timePerUnitMinutes: undefined,
          config: '{}',
        });
        setCronPreset(cronPresets[0].value);
      }
    }
  }, [open, automation, reset]);

  const createMutation = useMutation({
    mutationFn: (data: CreateAutomationPayload) => automationsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      onOpenChange(false);
      reset();
      toast.success('Automação criada!', 'A automação foi criada com sucesso.');
    },
    onError: (error: AxiosErrorResponse) => {
      toast.error('Erro ao criar automação', error.response?.data?.error?.message || error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CreateAutomationPayload) => automationsAPI.update(automation!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      onOpenChange(false);
      reset();
      toast.success('Automação atualizada!', 'As alterações foram salvas.');
    },
    onError: (error: AxiosErrorResponse) => {
      toast.error(
        'Erro ao atualizar automação',
        error.response?.data?.error?.message || error.message
      );
    },
  });

  const onSubmit = (data: AutomationFormInput) => {
    // Parse config de string para objeto antes de enviar
    let parsedConfig: Record<string, unknown> = {};
    try {
      parsedConfig = data.config && data.config.trim() !== '' ? JSON.parse(data.config) : {};
    } catch (e) {
      toast.error('Erro ao processar configuração', 'JSON inválido');
      return;
    }

    // Adicionar jobType e métricas de tempo ao config
    if (data.jobType) {
      parsedConfig.jobType = data.jobType;
    }

    // Adicionar métricas de tempo dentro de configTimeSaved (novo formato)
    if (
      (data.baseTimeMinutes !== undefined && !isNaN(data.baseTimeMinutes)) ||
      (data.timePerUnitMinutes !== undefined && !isNaN(data.timePerUnitMinutes))
    ) {
      parsedConfig.configTimeSaved = {
        baseTimeMinutes: data.baseTimeMinutes || 0,
        timePerUnitMinutes: data.timePerUnitMinutes || 0,
      };
    }

    const formData: CreateAutomationPayload = {
      type: data.type,
      name: data.name,
      description: data.description,
      isActive: data.isActive,
      config: Object.keys(parsedConfig).length > 0 ? parsedConfig : undefined,
    };

    // Add schedule only for CRON type
    if (data.type === AutomationType.CRON && data.schedule) {
      formData.schedule = data.schedule;
    }

    // Add slug only for WEBHOOK type (or optionally for CRON)
    if (data.slug) {
      formData.slug = data.slug;
    }

    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleCronPresetChange = (value: string) => {
    setCronPreset(value);
    if (value !== 'custom') {
      setValue('schedule', value);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogClose onClick={() => onOpenChange(false)} />

        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Automação' : 'Nova Automação'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Atualize os dados da automação'
              : 'Crie uma nova automação para executar tarefas automaticamente'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-6">
          {/* Type Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Tipo de Automação *</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setValue('type', AutomationType.CRON)}
                className={`p-4 border-2 rounded-lg transition-all ${
                  type === AutomationType.CRON
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-border hover:border-blue-300'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <Calendar
                    className={`h-6 w-6 ${type === AutomationType.CRON ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground'}`}
                  />
                  <span
                    className={`font-medium ${type === AutomationType.CRON ? 'text-blue-600 dark:text-blue-400' : 'text-foreground'}`}
                  >
                    Scheduler
                  </span>
                  <span className="text-xs text-muted-foreground text-center">
                    Executado em horários agendados
                  </span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setValue('type', AutomationType.WEBHOOK)}
                className={`p-4 border-2 rounded-lg transition-all ${
                  type === AutomationType.WEBHOOK
                    ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-border hover:border-purple-300'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <Webhook
                    className={`h-6 w-6 ${type === AutomationType.WEBHOOK ? 'text-purple-600 dark:text-purple-400' : 'text-muted-foreground'}`}
                  />
                  <span
                    className={`font-medium ${type === AutomationType.WEBHOOK ? 'text-purple-600 dark:text-purple-400' : 'text-foreground'}`}
                  >
                    Event
                  </span>
                  <span className="text-xs text-muted-foreground text-center">
                    Disparado por eventos externos
                  </span>
                </div>
              </button>
            </div>
            {errors.type && <p className="text-sm text-destructive">{errors.type.message}</p>}
          </div>

          {/* Active Status */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium text-foreground">Status</p>
              <p className="text-sm text-muted-foreground">
                Automação {isActive ? 'ativa' : 'inativa'}
              </p>
            </div>
            <Switch
              checked={isActive}
              onCheckedChange={(checked) => setValue('isActive', checked)}
            />
          </div>

          {/* Name */}
          <Input
            label="Nome *"
            placeholder={
              type === AutomationType.CRON
                ? 'Ex: Sincronizar dados diariamente'
                : 'Ex: Notificar nova submissão'
            }
            error={errors.name?.message}
            {...register('name')}
          />

          {/* Description */}
          <Textarea
            label="Descrição"
            placeholder="Descreva o que essa automação faz..."
            rows={3}
            error={errors.description?.message}
            {...register('description')}
          />

          {/* Slug */}
          <div>
            <Input
              label={`Slug ${type === AutomationType.WEBHOOK ? '*' : '(opcional)'}`}
              placeholder="ex: minha-automacao"
              error={errors.slug?.message}
              {...register('slug')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {type === AutomationType.WEBHOOK
                ? '⚠️ Este será o endpoint do webhook. Apenas letras minúsculas, números e hífens.'
                : '💡 Identificador único para esta automação (opcional).'}
            </p>
          </div>

          {/* Schedule (only for CRON) */}
          {type === AutomationType.CRON && (
            <div className="space-y-2 border-t pt-4">
              <label className="text-sm font-medium text-foreground">Agendamento (Cron) *</label>

              {/* Preset selector */}
              <Select
                options={cronPresets}
                value={cronPreset}
                onChange={(e) => handleCronPresetChange(e.target.value)}
              />

              {/* Custom cron input */}
              {cronPreset === 'custom' && (
                <Input
                  placeholder="0 9 * * * (formato cron)"
                  error={errors.schedule?.message}
                  {...register('schedule')}
                />
              )}

              {schedule && (
                <p className="text-xs text-muted-foreground">📅 Expressão: {schedule}</p>
              )}
            </div>
          )}

          {/* Job Type Selector */}
          <div className="space-y-2 border-t pt-4">
            <label className="text-sm font-medium text-foreground">Tipo de Job</label>
            <Select
              options={[
                { label: 'Selecione um job...', value: '' },
                ...jobs.map((job) => ({
                  label: `${job.metadata?.icon || '⚙️'} ${job.name}`,
                  value: job.id,
                })),
              ]}
              value={jobType || ''}
              onChange={(e) => setValue('jobType', e.target.value)}
            />
            {jobType && (
              <div className="bg-muted p-3 rounded-lg text-sm">
                <p className="text-foreground font-medium">
                  {jobs.find((j) => j.id === jobType)?.name}
                </p>
                <p className="text-muted-foreground mt-1">
                  {jobs.find((j) => j.id === jobType)?.description}
                </p>
              </div>
            )}
            {errors.jobType && <p className="text-sm text-destructive">{errors.jobType.message}</p>}
          </div>

          {/* Time Metrics */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-sm font-medium text-foreground">Métricas de Tempo</h3>
            <p className="text-xs text-muted-foreground">
              Configure quanto tempo esta automação economiza quando executada
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  label="Tempo Base (minutos)"
                  type="number"
                  min="0"
                  placeholder="Ex: 5"
                  {...register('baseTimeMinutes', { valueAsNumber: true })}
                />
              </div>
              <div>
                <Input
                  label="Tempo por Unidade (minutos)"
                  type="number"
                  min="0"
                  placeholder="Ex: 1"
                  {...register('timePerUnitMinutes', { valueAsNumber: true })}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              💡 Exemplo: Se o tempo base é 5min e tempo por unidade é 1min, ao processar 10 itens
              economiza 5 + (1 × 10) = 15 minutos
            </p>
          </div>

          {/* Config (JSON) */}
          <Controller
            name="config"
            control={control}
            render={({ field }) => (
              <JsonEditor
                label="Configuração Avançada (JSON)"
                height="400px"
                error={errors.config?.message}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending || updateMutation.isPending
                ? 'Salvando...'
                : isEdit
                  ? 'Atualizar'
                  : 'Criar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
